// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import { Dialog, DialogContent, DialogTitle, FormControl, IconButton, MenuItem, Select } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { Alert, AlertTitle } from '@material-ui/lab';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Close as CloseIcon } from '@material-ui/icons';

// Internal Dependencies
import { IfEmptyThrowError, IfFalseThrowError, IfNotEmptyThrowError, IsEmpty, IsString } from '../../../../common/checks';
import MText from '../../../../components/Common/MText';
import MButton from '../../../../components/MButton';
import OverLoading from '../../../../components/Loading';
import Colours from '../../../../styles/colours';
import ApiService from '../../../../services/apiService';
import MDropzone from '../../../../components/Common/form/MDropzone';
import Constants, { SamplePromoCodeUploadCsvUrl } from '../../../../common/constants';
import { IsCsvFile, parseCsvFile, Trim } from '../../../../common/util';

const promoCodeUploadSampleImg = require('../../../../assets/images/promo-code-upload-sample.png');

function CreateModal(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [giftId, setGiftId] = useState('');
  const [outletId, setOutletId] = useState('');
  const [promoCodes, setPromoCodes] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const isSubmitDisabled = IsEmpty(promoCodes) || IsEmpty(giftId) || IsEmpty(outletId);

  const onModalClose = () => {
    setErrorMessage('');
    setGiftId('');
    setOutletId('');
    setPromoCodes([]);
    props.onClose();
  };

  /**
   * Special function for parsing date from the csv file
   * If it is a string, it must be in DD/MM/YYYY format
   * It returns a moment object
   */
  const parseDate = (input) => {
    if (IsString(input)) {
      return moment(Trim(input, false, false, false), 'DD/MM/YYYY', false);
    }
    throw new Error('Invalid date input');
  };

  const onFileUpload = async ([file]) => {
    try {
      const codesArray = [];

      // file is cleared/removed
      if (IsEmpty(file)) {
        setPromoCodes([]);
        return;
      }

      IfFalseThrowError(IsCsvFile(file), 'Uploaded file must be a .csv file');

      const rows = await parseCsvFile(file);

      const code = [];

      _.map(rows, (row, index) => {
        const rowNumber = index + 1;

        const promoCode = row['Promo Code'];
        const expiryDate = row['Expiry Date'];

        IfEmptyThrowError(promoCode, `Empty promo code on row ${rowNumber}`);
        IfEmptyThrowError(expiryDate, `Empty expiry date on row ${rowNumber}`);
        IfFalseThrowError(parseDate(expiryDate).isValid(), `Invalid expiry date on row ${rowNumber}`);

        codesArray.push(promoCode);

        // Standardize the expiryDate in Singapore timezone
        code.push({
          code:       promoCode.toString(),
          expiryDate: parseDate(expiryDate).utcOffset('+08').endOf('day'),
        });
      });

      // check for duplicate promoCode
      const countCode = _.countBy(codesArray);
      const duplicates = _.keys(_.pickBy(countCode, (count) => count > 1));
      IfNotEmptyThrowError(duplicates, `Promo code ${duplicates.join(',')} are duplicate`);

      setPromoCodes(code);
    } catch (err) {
      setPromoCodes([]);
      toast.error(err.message);
    }
  };

  const onSubmitPromoCode = async () => {
    try {
      setIsLoading(true);

      const data = {
        giftId,
        promoCodes: JSON.stringify(promoCodes),
      };

      if (!IsEmpty(outletId)) {
        data.outletId = outletId;
      }

      await ApiService.createPromoCodes(props.page.selected.id, data);
      onModalClose();
      toast.success('Promo codes uploaded sucessfully');

      props.onPromoCodeUploaded();
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={onModalClose}
        maxWidth="sm"
        fullWidth
      >
        { isLoading && <OverLoading />}
        <DialogTitle>
          <div className="d-flex justify-content-between align-items-center">
            <MText
              bold
              variant="h2"
              size={16}
              text="Upload Promo Codes"
            />
            <IconButton size="medium" onClick={onModalClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="pb-4">
          <FormControl className="w-100 mb-2">
            <MText
              size={11}
              colour={Colours.Gray}
              text="1) Select Voucher/Gift"
            />
            <Select
              variant="outlined"
              style={{ height: 40 }}
              value={giftId}
              onChange={(e) => setGiftId(e.target.value)}
            >
              {
                IsEmpty(props.gifts) ? (
                  <MenuItem style={{ fontSize: 12 }} value="">
                    <em>None</em>
                  </MenuItem>
                ) : _.map(props.gifts, (gift) => (
                  <MenuItem key={gift.id} style={{ fontSize: 12 }} value={gift.id}>
                    {gift.label}
                      &nbsp; - &nbsp;
                    {gift.id}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>

          <FormControl className="w-100 mt-2">
            <MText
              size={11}
              colour={Colours.Gray}
              text="2) Select Outlet"
            />
            <Select
              style={{ height: 40 }}
              variant="outlined"
              value={outletId}
              onChange={(e) => setOutletId(e.target.value)}
            >
              {
                IsEmpty(props.outlets) ? (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )
                  : _.map(props.outlets, (outlet) => (
                    <MenuItem
                      key={outlet.id}
                      style={{ fontSize: 12 }}
                      value={outlet.id}
                    >
                      {outlet.name}
                    </MenuItem>
                  ))
              }
            </Select>
          </FormControl>

          <MText text="3) Upload promo codes file" className="mt-3" size={11} colour={Colours.Gray} />
          <Alert className="my-2" severity="info">
            <AlertTitle>Upload guideline</AlertTitle>
            <ul className="pl-0">
              <li>
                Upload a
                {' '}
                <strong>.csv</strong>
                {' '}
                file that follows the format shown in this
                {' '}
                <a
                  href={SamplePromoCodeUploadCsvUrl}
                  download="Sample Upload File.csv"
                  style={{ color: Colours.Blue, fontWeight: 'bold' }}
                >
                  file
                </a>
                .
              </li>
              <li>
                Expiry date format should follow&nbsp;
                <strong>DD/MM/YYYY</strong>
              </li>
            </ul>
          </Alert>

          <div className="w-100 my-2 d-flex flex-column justify-content-center align-items-center">
            <MText text="Sample file data" />
            <img
              src={promoCodeUploadSampleImg}
              alt="sample promo codes"
              draggable={false}
              style={{ height: '120px', userSelect: 'none' }}
            />
          </div>

          <div className="w-100 mt-3 mb-1 d-flex justify-content-center">
            <MDropzone
              maxFiles={1}
              maxFileSize={Constants.BinaryOneMBSize * 3}
              acceptedFiles={['text/csv']}
              displayText="Click or drag a CSV file here to upload"
              onChange={onFileUpload}
            />
          </div>

          {
            !IsEmpty(errorMessage) && (
              <Alert className="my-2 overflow-auto" severity="error" style={{ maxHeight: '80px' }}>
                <AlertTitle>
                  {errorMessage.split('.')[0]}
                  .
                </AlertTitle>
                {errorMessage.split('.').length > 1 ? errorMessage.split('.')[1] : ''}
              </Alert>
            )
          }

          <div className="w-100 mt-4 mb-5 d-flex justify-content-center">
            <MButton
              className="px-5"
              variant="contained"
              disabled={isSubmitDisabled}
              colour={isSubmitDisabled ? Colours.LightGray2 : Colours.Secondary}
              text="Upload"
              textColour={Colours.White}
              onClick={onSubmitPromoCode}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

CreateModal.propTypes = {
  open:    PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  outlets: PropTypes.arrayOf(PropTypes.shape(Object)),
  page:    PropTypes.shape({
    selected: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  gifts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  onPromoCodeUploaded: PropTypes.func.isRequired,
};

CreateModal.defaultProps = {
  outlets: [],
};

const mapStateToProps = (state) => ({
  page:  state.page,
  gifts: state.promoCode.gifts,
});

export default connect(mapStateToProps, null)(CreateModal);
